import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { Section } from '@sorare/core/src/components/layout/MainAsideLayout';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { PlayerProjectionInGameHeader } from '#src/components/game/Game/PlayerProjectionInGameHeader/index.tsx';
import { Game } from '#src/components/game/Game/index.tsx';

import type { Matches_anyGameInterface } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  noMatches: {
    defaultMessage: 'There are no matches for {displayName}',
  },
});

const Root = styled(FullWidthContainer)`
  --game-height-approx: 120px;
  padding-bottom: var(--double-unit);
  padding-top: var(--double-unit);
`;

type Props = {
  playerSlug?: string;
  displayName: string;
  loading: boolean;
  pastGames: Matches_anyGameInterface[];
  infiniteScrollLoader: ReactNode;
  futureGames: Matches_anyGameInterface[];
  tab: 'past' | 'future';
  setTab: (tab: 'past' | 'future') => void;
  onClick?: (id: string) => void;
};
export const Matches = ({
  playerSlug,
  displayName,
  loading,
  pastGames,
  infiniteScrollLoader,
  futureGames,
  tab,
  setTab,
  onClick,
}: Props) => {
  const isLaptop = useIsLaptop();
  const noGames = !loading && !pastGames.length && !futureGames.length;

  if (noGames) {
    return (
      <Section topPadding>
        <FullWidthContainer>
          <Warning
            title={messages.noMatches}
            variant="yellow"
            titleValues={{ displayName }}
          />
        </FullWidthContainer>
      </Section>
    );
  }

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }
  const firstUpcomingGame = futureGames[0];
  const upcomingGames = futureGames.slice(1);

  return (
    <Root>
      <Vertical gap={3}>
        {!!firstUpcomingGame && (
          <Vertical gap={1}>
            <HeadlineS>
              <FormattedMessage defaultMessage="Next match" />
            </HeadlineS>
            <Game
              game={firstUpcomingGame}
              displayDay
              onClick={() => onClick?.(firstUpcomingGame.id)}
              topRight={
                !!playerSlug && (
                  <PlayerProjectionInGameHeader
                    playerSlug={playerSlug}
                    anyGameId={firstUpcomingGame.id}
                  />
                )
              }
            />
          </Vertical>
        )}
        {!!(pastGames.length || upcomingGames.length > 1) && (
          <Vertical gap={1}>
            <HeadlineS>
              <FormattedMessage
                defaultMessage="{displayName} matches"
                values={{ displayName }}
              />
            </HeadlineS>
            <TabBar
              variant="primary"
              value={tab}
              fullWidth={!isLaptop}
              forceMobileLayout
            >
              <Tab
                value="past"
                onClick={() => setTab('past')}
                disabled={!pastGames.length}
              >
                <FormattedMessage {...fantasy.past} />
              </Tab>
              <Tab
                value="future"
                onClick={() => setTab('future')}
                disabled={!upcomingGames.length}
              >
                <FormattedMessage {...fantasy.upcoming} />
              </Tab>
            </TabBar>
            <Vertical>
              {tab === 'future'
                ? upcomingGames.map((game, index) => (
                    <Game
                      key={game.id}
                      game={game}
                      onClick={() => onClick?.(game.id)}
                      displayDay
                      selected={futureGames.length === index + 1}
                      topRight={
                        !!playerSlug && (
                          <PlayerProjectionInGameHeader
                            playerSlug={playerSlug}
                            anyGameId={game.id}
                          />
                        )
                      }
                    />
                  ))
                : pastGames.map(game => (
                    <Game
                      key={game.id}
                      displayDay
                      onClick={() => onClick?.(game.id)}
                      game={game}
                      topRight={
                        !!playerSlug && (
                          <PlayerProjectionInGameHeader
                            playerSlug={playerSlug}
                            anyGameId={game.id}
                          />
                        )
                      }
                    />
                  ))}
            </Vertical>
            {!loading && infiniteScrollLoader}
          </Vertical>
        )}
      </Vertical>
    </Root>
  );
};

Matches.fragments = {
  anyGameInterface: gql`
    fragment Matches_anyGameInterface on AnyGameInterface {
      id
      ...Game_anyGameInterface
    }
    ${Game.fragments.anyGameInterface}
  ` as TypedDocumentNode<Matches_anyGameInterface>,
};
